import * as React from 'react';
import { useSelector } from 'react-redux';
import { betreuerByNrSelector, betreuerSelector, lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { CommandBarButton, PrimaryButton, TextField } from '@fluentui/react';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import moment from 'moment-timezone';
import { SelectOption } from 'formComponents/Select/types';
import { ValueType } from 'react-select';
import { Select } from 'formComponents/Select/Select';
import { authSelector } from 'store/auth/selector';
import { Label } from '@fluentui/react/lib/Label';
import { formStyles } from 'styles/formStyles';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { MultiSelect } from 'formComponents/Select/MultiSelect';
import { useBranchenAsOptions } from 'api/branche';
import { useHistory, useLocation } from 'react-router-dom';
import { useErweiterteSuchenAsOptions } from 'api/erweiterteSuche';
import { useInteresseFuerProdukteAsOptions } from 'api/interesseFuerProdukte';
import { useProduktgruppenAsOptions } from 'api/produktgruppe';
import { FormDecimalField } from 'formComponents/FormDecimalField';
import { KundeSelect } from 'components/Kunde/KundeSelect';
import { getKunde } from 'api/kunde';
import { Helmet } from 'react-helmet';
import { isEqual } from 'lodash';
import { generateReportPrintUrl } from '../utils';
import { FilterValues, groupByOptions, sourceOptions, UmsatzSummaryGroupBy, UmsatzSummarySource } from './types';
import { Umsatzuebersicht } from './Umsatzuebersicht';
import { UmsatzuebersichtList } from './UmsatzuebersichtList';

const styles = mergeStyleSets({
    container: {
        margin: 0,
        marginTop: 5,
        padding: 0,
        display: 'grid',
        gridTemplateRows: 'max-content max-content max-content 1fr',
        gridRowGap: 5,
        height: 'calc(100vh - 75px)',
    },
    searchLine1: {
        display: 'grid',
        gridTemplateColumns: '130px 130px 160px 160px 180px 180px 1fr 1fr 1fr 1fr max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchLine2: {
        display: 'grid',
        gridTemplateColumns: '100px 100px 100px 100px 180px 180px 1fr 1fr 1fr',
        gridColumnGap: 10,
    },
    searchLine3: {
        display: 'grid',
        gridTemplateColumns: 'repeat(12, max-content) 1fr',
        gridColumnGap: 10,
    },
    actionButtons: {
        marginTop: 29,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, max-content)',
        gridColumnGap: 10,
    },
    reset: {
        textAlign: 'right',
        marginTop: 8,
    },
});

const alleBetreuerOption = { value: '__all__', label: 'Alle Betreuer' };

const defaultFilterValues: FilterValues = {
    source: 'auftrag',
    groupBy: 'kunde',
    sortColumn: 'groupByLabel',
    sortDirection: 'asc',
    datumVon: moment().startOf('month').format('YYYY-MM-DD'), // '2021-10-01'
    datumBis: moment().format('YYYY-MM-DD'), // '2021-10-xx'
    kNr: undefined,
    konzernNr: undefined,
    kName: '',
    kundePlzVon: '',
    kundePlzBis: '',
    kundeUmsatzTsdVon: 0,
    kundeUmsatzTsdBis: 0,
    kundeBranchen: [],
    kundeErweiterteSuchen: [],
    kundeInteresseFuerProdukte: [],
    kundeBetreuer: undefined,
    kundeTelBetreuer: undefined,
    betreuer: undefined,
    betreuerGebiet: false,
    telBetreuer: undefined,
    telBetreuerGebiet: false,
    produktgruppen: [],
    showDetails: false,
    addMissingRows1: true,
    addMissingRows3: true,
    showEkNetto: true,
    showVkNetto: true,
    showBMarge: true,
    showNMarge: true,
    showBAnteil: true,
    showFAnteil: true,
};

export const UmsatzuebersichtView: React.FC = () => {
    const auth = useSelector(authSelector);
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const betreuer = useSelector(betreuerSelector);
    const betreuerByNr = useSelector(betreuerByNrSelector);
    const branchenOptions = useBranchenAsOptions(true);
    const interesseFuerProdukteOptions = useInteresseFuerProdukteAsOptions(true);
    const erweiterteSuchenOptions = useErweiterteSuchenAsOptions(true);
    const produktgruppenOptions = useProduktgruppenAsOptions();
    const [filterValues, setFilterValues] = React.useState<FilterValues>({
        ...defaultFilterValues,
    });
    const [appliedFilterValues, setAppliedFilterValues] = React.useState<FilterValues | null>(null);
    const isApplied = isEqual(filterValues, appliedFilterValues);

    const history = useHistory();
    const location = useLocation();

    const urlSearchParams = new URLSearchParams(location.search);
    const urlKNr = urlSearchParams.get('kNr');

    React.useEffect(() => {
        if (urlKNr) {
            history.replace({
                search: '',
            });

            const kNr = Number.parseInt(urlKNr, 10) || 0;
            if (kNr) {
                getKunde({ nr: kNr }).then((kunden) => {
                    if (kunden?.length === 1) {
                        setFilterValues({ ...filterValues, kNr: kunden[0].nr, kName: kunden[0].name1 });
                    }
                });
            }
        }
    }, [filterValues, history, location, setFilterValues, urlKNr]);

    const betreuerFilterOptions = React.useMemo<SelectOption[]>(() => {
        const options: SelectOption[] = [];

        if (auth.rolle === 'AD') {
            options.push({
                value: auth.nr?.toString() || '',
                label: auth.name || '?',
            });
        } else {
            options.push(alleBetreuerOption);
            betreuer
                .filter((b) => b.aktiv && b.name)
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach((b) => options.push({ value: b.nr.toString(), label: b.name }));
        }

        return options;
    }, [auth, betreuer]);

    const kundeBranchenValues = React.useMemo(
        () => branchenOptions.filter((o) => filterValues.kundeBranchen?.includes(parseInt(o.value, 10))),
        [branchenOptions, filterValues.kundeBranchen],
    );

    const kundeInteresseFuerProdukteValues = React.useMemo(
        () =>
            interesseFuerProdukteOptions.filter((o) =>
                filterValues.kundeInteresseFuerProdukte?.includes(parseInt(o.value, 10)),
            ),
        [interesseFuerProdukteOptions, filterValues.kundeInteresseFuerProdukte],
    );

    const kundeErweiterteSuchenValues = React.useMemo(
        () =>
            erweiterteSuchenOptions.filter((o) => filterValues.kundeErweiterteSuchen?.includes(parseInt(o.value, 10))),
        [erweiterteSuchenOptions, filterValues.kundeErweiterteSuchen],
    );

    const produktgruppenValues = React.useMemo(
        () => produktgruppenOptions.filter((o) => filterValues.produktgruppen?.includes(parseInt(o.value, 10))),
        [produktgruppenOptions, filterValues.produktgruppen],
    );

    const sourceLabel = sourceOptions.find((o) => o.value === filterValues.source)?.label;

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={styles.container}>
            <Helmet>
                <title>Umsatzübersicht</title>
            </Helmet>
            <div className={styles.searchLine1}>
                <div>
                    <Label>Auftr./Rechn./Best.</Label>
                    <Select
                        name="source"
                        value={sourceOptions.find((o) => o.value === filterValues.source)}
                        options={sourceOptions}
                        onChange={(option: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                source: (option?.value || 'auftrag') as UmsatzSummarySource,
                            });
                        }}
                    />
                </div>

                <div>
                    <Label>Gruppierung</Label>
                    <Select
                        name="groupBy"
                        value={groupByOptions.find((o) => o.value === filterValues.groupBy)}
                        options={groupByOptions}
                        onChange={(option: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                groupBy: (option?.value || 'produkt') as UmsatzSummaryGroupBy,
                            });
                        }}
                    />
                </div>

                <FormDatePicker
                    value={filterValues.datumVon}
                    label={'Datum Von (' + sourceLabel + ')'}
                    options={{
                        required: true,
                    }}
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            datumVon: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                        });
                    }}
                />

                <FormDatePicker
                    value={filterValues.datumBis}
                    label={'Datum Bis (' + sourceLabel + ')'}
                    options={{
                        required: true,
                    }}
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            datumBis: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                        });
                    }}
                />

                <div>
                    <Label>Betreuer ({sourceLabel})</Label>
                    <Select
                        name="betreuer"
                        value={
                            filterValues.betreuer
                                ? {
                                      value: filterValues.betreuer.toString(),
                                      label: betreuerByNr[filterValues.betreuer]?.name,
                                  }
                                : alleBetreuerOption
                        }
                        options={betreuerFilterOptions}
                        onChange={(newValue: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                betreuer:
                                    newValue?.value && newValue?.value !== '__all__'
                                        ? parseInt(newValue?.value, 10)
                                        : undefined,
                            });
                        }}
                    />
                </div>

                <div>
                    <Label>Tel. Betreuer ({sourceLabel})</Label>
                    <Select
                        name="telBetreuer"
                        value={
                            filterValues.telBetreuer
                                ? {
                                      value: filterValues.telBetreuer.toString(),
                                      label: betreuerByNr[filterValues.telBetreuer]?.name,
                                  }
                                : alleBetreuerOption
                        }
                        options={betreuerFilterOptions}
                        onChange={(newValue: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                telBetreuer:
                                    newValue?.value && newValue?.value !== '__all__'
                                        ? parseInt(newValue?.value, 10)
                                        : undefined,
                            });
                        }}
                    />
                </div>

                <div>
                    <Label>Produktgruppen</Label>
                    <MultiSelect
                        name="produktgruppen"
                        value={produktgruppenValues}
                        placeholder="Produktgruppen..."
                        options={produktgruppenOptions}
                        onChange={(values: ValueType<SelectOption, true>): void => {
                            setFilterValues({
                                ...filterValues,
                                produktgruppen: ((values || []) as SelectOption[]).map((o) => parseInt(o.value, 10)),
                            });
                        }}
                    />
                </div>

                <KundeSelect
                    label="Hauptlieferant"
                    kNr={filterValues.hauptlieferantNr}
                    name={filterValues.hauptlieferantName}
                    allowEmpty
                    onChange={(newKNr, newKunde): void => {
                        setFilterValues({
                            ...filterValues,
                            hauptlieferantNr: newKNr || undefined,
                            hauptlieferantName: newKunde?.name1 || '',
                        });
                    }}
                />

                <KundeSelect
                    label="Kunde"
                    kNr={filterValues.kNr}
                    name={filterValues.kName}
                    allowEmpty
                    onChange={(newKNr, newKunde): void => {
                        setFilterValues({
                            ...filterValues,
                            kNr: newKNr || undefined,
                            kName: newKunde?.name1 || '',
                        });
                    }}
                />

                <KundeSelect
                    label="Konzern"
                    kNr={filterValues.konzernNr}
                    name={filterValues.konzernName}
                    allowEmpty
                    onChange={(newKNr, newKunde): void => {
                        setFilterValues({
                            ...filterValues,
                            konzernNr: newKNr || undefined,
                            konzernName: newKunde?.name1 || '',
                        });
                    }}
                />

                <div className={styles.actionButtons}>
                    {auth.rolle !== 'AD' && auth.rolle !== 'EX' && (
                        <PrimaryButton
                            disabled={!isApplied}
                            onClick={() => {
                                window.open(
                                    generateReportPrintUrl('umsatzuebersicht', {
                                        source: filterValues.source,
                                        groupBy: filterValues.groupBy,
                                        sortColumn: filterValues.sortColumn,
                                        sortDirection: filterValues.sortDirection,
                                        datumVon: filterValues.datumVon,
                                        datumBis: filterValues.datumBis,
                                        kNr: filterValues.kNr,
                                        kName: filterValues.kName,
                                        konzernNr: filterValues.konzernNr,
                                        konzernName: filterValues.konzernName,
                                        kundePlzVon: filterValues.kundePlzVon,
                                        kundePlzBis: filterValues.kundePlzBis,
                                        kundeUmsatzTsdVon: filterValues.kundeUmsatzTsdVon,
                                        kundeUmsatzTsdBis: filterValues.kundeUmsatzTsdBis,
                                        kundeBranchen: (filterValues.kundeBranchen || []).join(','),
                                        kundeInteresseFuerProdukte: (
                                            filterValues.kundeInteresseFuerProdukte || []
                                        ).join(','),
                                        kundeErweiterteSuchen: (filterValues.kundeErweiterteSuchen || []).join(','),
                                        kundeBetreuer: filterValues.kundeBetreuer,
                                        kundeTelBetreuer: filterValues.kundeTelBetreuer,
                                        betreuer: filterValues.betreuer,
                                        betreuerGebiet: filterValues.betreuerGebiet,
                                        telBetreuer: filterValues.telBetreuer,
                                        telBetreuerGebiet: filterValues.telBetreuerGebiet,
                                        produktgruppen: (filterValues.produktgruppen || []).join(','),
                                        hauptlieferantNr: filterValues.hauptlieferantNr,
                                        hauptlieferantName: filterValues.hauptlieferantName,
                                        showDetails: filterValues.showDetails,
                                        addMissingRows1: filterValues.addMissingRows1,
                                        addMissingRows3: filterValues.addMissingRows3,
                                        showEkNetto: filterValues.showEkNetto,
                                        showVkNetto: filterValues.showVkNetto,
                                        showBMarge: filterValues.showBMarge,
                                        showNMarge: filterValues.showNMarge,
                                        showBAnteil: filterValues.showBAnteil,
                                        showFAnteil: filterValues.showFAnteil,
                                        hideGroupByLabel: filterValues.hideGroupByLabel,
                                    }),
                                );
                            }}
                            text="Drucken"
                        />
                    )}
                    <PrimaryButton
                        text="Start"
                        disabled={isApplied}
                        onClick={() => setAppliedFilterValues(filterValues)}
                    />
                </div>
            </div>

            <div className={styles.searchLine2}>
                <TextField
                    placeholder="K-PLZ (von)"
                    value={filterValues.kundePlzVon}
                    onChange={(event, newValue) => {
                        setFilterValues({
                            ...filterValues,
                            kundePlzVon: newValue || '',
                        });
                    }}
                />
                <TextField
                    placeholder="K-PLZ (bis)"
                    value={filterValues.kundePlzBis}
                    onChange={(event, newValue) => {
                        setFilterValues({
                            ...filterValues,
                            kundePlzBis: newValue || '',
                        });
                    }}
                />

                <FormDecimalField
                    placeholder="K-Umsatz (von TSD)"
                    value={filterValues.kundeUmsatzTsdVon}
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            kundeUmsatzTsdVon: newValue,
                        });
                    }}
                />

                <FormDecimalField
                    placeholder="K-Umsatz (bis TSD)"
                    value={filterValues.kundeUmsatzTsdBis}
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            kundeUmsatzTsdBis: newValue,
                        });
                    }}
                />

                <div>
                    <Select
                        name="kundeBetreuer"
                        value={
                            filterValues.kundeBetreuer
                                ? {
                                      value: filterValues.kundeBetreuer.toString(),
                                      label: betreuerByNr[filterValues.kundeBetreuer]?.name,
                                  }
                                : alleBetreuerOption
                        }
                        options={betreuerFilterOptions}
                        onChange={(newValue: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                kundeBetreuer:
                                    newValue?.value && newValue?.value !== '__all__'
                                        ? parseInt(newValue?.value, 10)
                                        : undefined,
                            });
                        }}
                    />
                </div>

                <div>
                    <Select
                        name="kundeTelBetreuer"
                        value={
                            filterValues.kundeTelBetreuer
                                ? {
                                      value: filterValues.kundeTelBetreuer.toString(),
                                      label: betreuerByNr[filterValues.kundeTelBetreuer]?.name,
                                  }
                                : alleBetreuerOption
                        }
                        options={betreuerFilterOptions}
                        onChange={(newValue: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                kundeTelBetreuer:
                                    newValue?.value && newValue?.value !== '__all__'
                                        ? parseInt(newValue?.value, 10)
                                        : undefined,
                            });
                        }}
                    />
                </div>

                <MultiSelect
                    name="filterBranche"
                    value={kundeBranchenValues}
                    placeholder="Branchen..."
                    options={branchenOptions}
                    onChange={(values: ValueType<SelectOption, true>): void => {
                        setFilterValues({
                            ...filterValues,
                            kundeBranchen: ((values || []) as SelectOption[]).map((o) => parseInt(o.value, 10)),
                        });
                    }}
                />

                <MultiSelect
                    name="filterInteresseFuerProdukte"
                    value={kundeInteresseFuerProdukteValues}
                    placeholder="Int. f. Prod..."
                    options={interesseFuerProdukteOptions}
                    onChange={(values: ValueType<SelectOption, true>): void => {
                        setFilterValues({
                            ...filterValues,
                            kundeInteresseFuerProdukte: ((values || []) as SelectOption[]).map((o) =>
                                parseInt(o.value, 10),
                            ),
                        });
                    }}
                />

                <MultiSelect
                    name="filterErweiterteSuchen"
                    value={kundeErweiterteSuchenValues}
                    placeholder="Erw. Suche..."
                    options={erweiterteSuchenOptions}
                    onChange={(values: ValueType<SelectOption, true>): void => {
                        setFilterValues({
                            ...filterValues,
                            kundeErweiterteSuchen: ((values || []) as SelectOption[]).map((o) => parseInt(o.value, 10)),
                        });
                    }}
                />
            </div>

            <div className={styles.searchLine3}>
                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="Betreuer - nur Gebiet"
                    disabled={!filterValues.betreuer || filterValues.showDetails}
                    checked={filterValues.betreuerGebiet}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            betreuerGebiet: !!checked,
                        });
                    }}
                />

                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="Tel.-Betr. - nur Gebiet"
                    disabled={!filterValues.telBetreuer || filterValues.showDetails}
                    checked={filterValues.telBetreuerGebiet}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            telBetreuerGebiet: !!checked,
                        });
                    }}
                />

                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="Details"
                    checked={filterValues.showDetails}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            showDetails: !!checked,
                        });
                    }}
                />

                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="Fehlende Einträge 1J"
                    disabled={filterValues.showDetails || filterValues.addMissingRows3}
                    checked={filterValues.addMissingRows1 || filterValues.addMissingRows3}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            addMissingRows1: !!checked,
                        });
                    }}
                />

                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="Fehlende Einträge 3J"
                    disabled={filterValues.showDetails}
                    checked={filterValues.addMissingRows3}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            addMissingRows1: true,
                            addMissingRows3: !!checked,
                        });
                    }}
                />

                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="EK Netto"
                    disabled={filterValues.showDetails}
                    checked={filterValues.showEkNetto}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            showEkNetto: !!checked,
                        });
                    }}
                />

                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="VK Netto"
                    disabled={filterValues.showDetails}
                    checked={filterValues.showVkNetto}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            showVkNetto: !!checked,
                        });
                    }}
                />

                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="B-Marge"
                    disabled={filterValues.showDetails}
                    checked={filterValues.showBMarge}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            showBMarge: !!checked,
                        });
                    }}
                />

                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="N-Marge"
                    disabled={filterValues.showDetails}
                    checked={filterValues.showNMarge}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            showNMarge: !!checked,
                        });
                    }}
                />

                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="B-Anteil"
                    disabled={filterValues.showDetails}
                    checked={filterValues.showBAnteil}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            showBAnteil: !!checked,
                        });
                    }}
                />

                {auth.rolle !== 'AD' && auth.rolle !== 'EX' && (
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="F-Anteil"
                        disabled={filterValues.showDetails}
                        checked={filterValues.showFAnteil}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                showFAnteil: !!checked,
                            });
                        }}
                    />
                )}

                {auth.rolle !== 'AD' && auth.rolle !== 'EX' && (
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Kunde ausblenden"
                        disabled={filterValues.showDetails}
                        checked={filterValues.hideGroupByLabel}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                hideGroupByLabel: !!checked,
                            });
                        }}
                    />
                )}

                <div className={styles.reset}>
                    <CommandBarButton text="Zurücksetzen" onClick={() => setFilterValues(defaultFilterValues)} />
                </div>
            </div>

            {appliedFilterValues && appliedFilterValues.showDetails && (
                <UmsatzuebersichtList
                    source={appliedFilterValues.source}
                    groupBy={appliedFilterValues.groupBy}
                    sortColumn={appliedFilterValues.sortColumn}
                    sortDirection={appliedFilterValues.sortDirection}
                    datumVon={appliedFilterValues.datumVon}
                    datumBis={appliedFilterValues.datumBis}
                    kNr={appliedFilterValues.kNr}
                    kName={appliedFilterValues.kName}
                    konzernNr={appliedFilterValues.konzernNr}
                    konzernName={appliedFilterValues.konzernName}
                    kundePlzVon={appliedFilterValues.kundePlzVon}
                    kundePlzBis={appliedFilterValues.kundePlzBis}
                    kundeUmsatzTsdVon={appliedFilterValues.kundeUmsatzTsdVon}
                    kundeUmsatzTsdBis={appliedFilterValues.kundeUmsatzTsdBis}
                    kundeBranchen={appliedFilterValues.kundeBranchen}
                    kundeInteresseFuerProdukte={appliedFilterValues.kundeInteresseFuerProdukte}
                    kundeErweiterteSuchen={appliedFilterValues.kundeErweiterteSuchen}
                    kundeBetreuer={appliedFilterValues.kundeBetreuer}
                    kundeTelBetreuer={appliedFilterValues.kundeTelBetreuer}
                    betreuer={appliedFilterValues.betreuer}
                    betreuerGebiet={appliedFilterValues.betreuerGebiet}
                    telBetreuer={appliedFilterValues.telBetreuer}
                    telBetreuerGebiet={appliedFilterValues.telBetreuerGebiet}
                    produktgruppen={appliedFilterValues.produktgruppen}
                    hauptlieferantNr={appliedFilterValues.hauptlieferantNr}
                    hauptlieferantName={appliedFilterValues.hauptlieferantName}
                    showDetails={appliedFilterValues.showDetails}
                    addMissingRows1={appliedFilterValues.addMissingRows1}
                    addMissingRows3={appliedFilterValues.addMissingRows3}
                />
            )}

            {appliedFilterValues && !appliedFilterValues.showDetails && (
                <Umsatzuebersicht
                    source={appliedFilterValues.source}
                    groupBy={appliedFilterValues.groupBy}
                    sortColumn={filterValues.sortColumn}
                    sortDirection={filterValues.sortDirection}
                    toggleSort={(sortColumn) => {
                        if (filterValues.sortColumn === sortColumn) {
                            setFilterValues({
                                ...filterValues,
                                sortColumn,
                                sortDirection: filterValues.sortDirection === 'asc' ? 'desc' : 'asc',
                            });
                        } else {
                            setFilterValues({ ...filterValues, sortColumn, sortDirection: 'desc' });
                        }
                    }}
                    datumVon={appliedFilterValues.datumVon}
                    datumBis={appliedFilterValues.datumBis}
                    kNr={appliedFilterValues.kNr}
                    kName={appliedFilterValues.kName}
                    konzernNr={appliedFilterValues.konzernNr}
                    konzernName={appliedFilterValues.konzernName}
                    kundePlzVon={appliedFilterValues.kundePlzVon}
                    kundePlzBis={appliedFilterValues.kundePlzBis}
                    kundeUmsatzTsdVon={appliedFilterValues.kundeUmsatzTsdVon}
                    kundeUmsatzTsdBis={appliedFilterValues.kundeUmsatzTsdBis}
                    kundeBranchen={appliedFilterValues.kundeBranchen}
                    kundeInteresseFuerProdukte={appliedFilterValues.kundeInteresseFuerProdukte}
                    kundeErweiterteSuchen={appliedFilterValues.kundeErweiterteSuchen}
                    kundeBetreuer={appliedFilterValues.kundeBetreuer}
                    kundeTelBetreuer={appliedFilterValues.kundeTelBetreuer}
                    betreuer={appliedFilterValues.betreuer}
                    betreuerGebiet={appliedFilterValues.betreuerGebiet}
                    telBetreuer={appliedFilterValues.telBetreuer}
                    telBetreuerGebiet={appliedFilterValues.telBetreuerGebiet}
                    produktgruppen={appliedFilterValues.produktgruppen}
                    hauptlieferantNr={appliedFilterValues.hauptlieferantNr}
                    hauptlieferantName={appliedFilterValues.hauptlieferantName}
                    showDetails={appliedFilterValues.showDetails}
                    addMissingRows1={appliedFilterValues.addMissingRows1}
                    addMissingRows3={appliedFilterValues.addMissingRows3}
                    showEkNetto={appliedFilterValues.showEkNetto}
                    showVkNetto={appliedFilterValues.showVkNetto}
                    showBMarge={appliedFilterValues.showBMarge}
                    showNMarge={appliedFilterValues.showNMarge}
                    showBAnteil={appliedFilterValues.showBAnteil}
                    showFAnteil={appliedFilterValues.showFAnteil}
                    hideGroupByLabel={appliedFilterValues.hideGroupByLabel}
                />
            )}
        </div>
    );
};
